/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { useText } from '@adobe-commerce/elsie/i18n';
import { classes } from '@adobe-commerce/elsie/lib';

import '@adobe-commerce/elsie/components/ProgressSpinner/ProgressSpinner.css';

/**
 * @param size this is the size of the spinner ranging in [12px, 24px, 36px, 48px]
 * @param stroke this is the stroke of the spinner ranging in [1px, 2px, 3px, 4px]
 */
export interface ProgressSpinnerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  ariaLabel?: string;
  size?: 'small' | 'medium' | 'big' | 'large';
  stroke?: '1' | '2' | '3' | '4';
}

export const ProgressSpinner: FunctionComponent<ProgressSpinnerProps> = ({
  ariaLabel,
  size = 'small',
  stroke = '4',
  children,
  className,
  style,
  ...props
}) => {
  const commonClasses = [
    'dropin-progress-spinner',
    `dropin-progress-spinner--shape-size-${size}`,
    `dropin-progress-spinner--shape-stroke-${stroke}`,
  ];

  const translations = useText({
    updating: 'Dropin.ProgressSpinner.updating.label',
    updatingChildren: 'Dropin.ProgressSpinner.updatingChildren.label',
  });

  const handleAriaLabel = () => {
    if (ariaLabel) return ariaLabel;
    return children ? translations.updatingChildren : translations.updating;
  };

  return children ? (
    // if we provide children to this component, it should act as a provider in order to add background with low opacity
    <div
      {...props}
      className={classes(['dropin-progress-spinner-provider'])}
      aria-live="polite"
      role="status"
    >
      <div aria-hidden={true}>{children}</div>
      <div
        aria-label={handleAriaLabel()}
        role="status"
        className={classes(['dropin-progress-spinner-background', className])}
        style={style}
      />
      <div
        className={classes([
          'dropin-progress-spinner-with-provider',
          ...commonClasses,
        ])}
        aria-hidden={true}
      />
    </div>
  ) : (
    // if there are no children, it should be able to be standalone and have no background
    <div
      {...props}
      className={classes([className, ...commonClasses])}
      aria-live="polite"
      role="status"
      aria-label={handleAriaLabel()}
    />
  );
};
